import { useContext } from 'react';

import AJAX from 'common/AJAX';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';

type QPValue = string | string[] | undefined;

type EncodedQuery = Record<string, string | string[] | undefined>;

type LocationWithQuery = {
  pathname: string;
  query: EncodedQuery;
};

type Props<QueryParams extends Record<string, QPValue>> = {
  getQueryParams: (location: LocationWithQuery) => QueryParams;
  getEncodedQueryParams: (params: QueryParams) => EncodedQuery;
  preferencesKey: string;
  // this is the queryParams from the redux store, used to avoid the delays to location.query updates caused by asyncConnect
  // this allows real-time updates to the filter controls when the user changes the filters
  queryState: EncodedQuery | null;
  reloadCompany: () => void;
};

const useFilterControls = <QueryParams extends Record<string, QPValue>>({
  getEncodedQueryParams,
  getQueryParams,
  preferencesKey,
  queryState,
  reloadCompany,
}: Props<QueryParams>) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  // queryState can be null on load
  const currentQuery = queryState ?? location.query;

  const queryParams = getQueryParams({ pathname: location.pathname, query: currentQuery });

  const updatePreferences = async (params: QueryParams) => {
    await AJAX.post('/api/viewer/updatePreferences', {
      preferences: {
        [preferencesKey]: params,
      },
    });
    reloadCompany();
  };

  const updateFilters = (params: QueryParams) => {
    updatePreferences(params);

    router.replace({
      pathname: location.pathname,
      query: getEncodedQueryParams(params),
    });
  };

  return {
    updateFilters,
    queryParams,
  };
};

export default useFilterControls;
